// hooks/usePlayers.ts
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { SupabaseQueryBuilder } from '../lib/supabaseQueryBuilder.ts';
import { QueryOptions, PaginatedResponse } from '../types/query.types.ts';
import { supabase } from '../lib/supabaseClient';
import { logAndThrow } from '../utils/logging';
import { Player } from '../types';
import { PLAYER_LIST_COLUMNS } from '../lib/players/privateData';

// Helper: map a Partial<Player> in camelCase to DB row shape.
function mapPlayerToDb(row: any) {
    if (!row) return row;
    const { currentTeamId, firstName, lastName, firstNameAr, lastNameAr, dateOfBirth, licenseNumber, isArchived, id } = row;
    return {
        id,
        first_name: firstName,
        last_name: lastName,
        first_name_ar: firstNameAr || null,
        last_name_ar: lastNameAr || null,
        date_of_birth: dateOfBirth || null,
        license_number: licenseNumber || null,
        current_team_id: currentTeamId || null,
        is_archived: isArchived ?? false,
        player_number: row.playerNumber || null,
    };
}

const PLAYERS_KEY = 'players';
/** Clé partagée par les invalidations (handlers temps réel). */
export const PLAYERS_LOOKUP_QUERY_KEY_PREFIX = 'players_lookup';

const PLAYERS_STALE_TIME = 30 * 60 * 1000; // 30 minutes
const PLAYERS_GC_TIME = 2 * 60 * 60 * 1000; // 2 hours

export function usePlayers(options: QueryOptions = {}) {
    const { enabled, ...queryOptions } = options;
    return useQuery<PaginatedResponse<Player>>({
        queryKey: [PLAYERS_KEY, queryOptions],
        queryFn: async () => {
            const { filters, pagination } = queryOptions;
            const leagueId = filters?.leagueId;
            // Handle array of leagueIds (take first) or single string
            const targetLeagueId = Array.isArray(leagueId) ? leagueId[0] : leagueId;

            if (targetLeagueId) {
                // Use optimized RPC
                const { data, error } = await supabase.rpc('get_league_players', {
                    p_league_id: targetLeagueId,
                    p_season: filters?.season || null,
                    p_search: filters?.search || null,
                    p_page: pagination?.page || 1,
                    p_page_size: pagination?.pageSize || 25,
                    p_team_id: filters?.currentTeamId || null,
                    p_include_archived: filters?.isArchived ?? false
                });

                if (error) {
                    console.error("RPC get_league_players failed:", error);
                    throw error;
                }

                const mapped = (data || []).map((row: any) => ({
                    id: row.id,
                    firstName: row.first_name,
                    lastName: row.last_name,
                    fullName: `${row.first_name} ${row.last_name}`,
                    firstNameAr: row.first_name_ar || undefined,
                    lastNameAr: row.last_name_ar || undefined,
                    dateOfBirth: row.date_of_birth,
                    licenseNumber: row.license_number,
                    playerNumber: row.player_number,
                    currentTeamId: row.current_team_id,
                    isArchived: row.is_archived,
                    createdAt: row.created_at,
                    createdBy: row.created_by,
                    updatedAt: row.updated_at,
                    updatedBy: row.updated_by,
                    currentTeam: row.current_team, // RPC returns JSONB
                    createdByName: undefined,
                    updatedByName: undefined
                } as Player));

                const count = data && data.length > 0 ? Number(data[0].total_count) : 0;
                const page = pagination?.page || 1;
                const pageSize = pagination?.pageSize || 25;

                return {
                    data: mapped,
                    count,
                    page,
                    pageSize,
                    totalPages: Math.ceil(count / pageSize),
                    hasMore: count > page * pageSize
                };
            }

            // Fallback to legacy query builder
            const paginated = await SupabaseQueryBuilder.executePaginatedQuery<any>(
                'players',
                // Colonnes nommées : jamais `*` ni de colonne personnelle (lib/players/privateData).
                `
          ${PLAYER_LIST_COLUMNS},
                    currentTeam:teams!players_current_team_id_fkey(*),
                    player_league_memberships!inner(league_id, season, is_active)
        `,
                {
                    ...queryOptions,
                    filters: {
                        ...queryOptions.filters,
                        isArchived: false
                    }
                }
            );
            // Map each row snake_case -> camelCase Player shape
            const mapped = paginated.data.map((data: any) => ({
                id: data.id,
                firstName: data.first_name,
                lastName: data.last_name,
                fullName: data.full_name || `${data.first_name} ${data.last_name}`,
                firstNameAr: data.first_name_ar || undefined,
                lastNameAr: data.last_name_ar || undefined,
                // Donnée personnelle, non lue ici (édition : fetchPlayerForEdit).
                dateOfBirth: undefined,
                licenseNumber: data.license_number,
                playerNumber: data.player_number,
                currentTeamId: data.current_team_id,
                isArchived: data.is_archived,
                createdAt: data.created_at,
                createdBy: data.created_by,
                createdByName: data.created_by_name,
                updatedAt: data.updated_at,
                updatedBy: data.updated_by,
                updatedByName: data.updated_by_name,
            } as Player));
            return { ...paginated, data: mapped };
        },
        enabled: enabled !== false,
        staleTime: PLAYERS_STALE_TIME,
        gcTime: PLAYERS_GC_TIME,
        placeholderData: keepPreviousData,
    });
}

/**
 * Lightweight hook for player lookups - fetches only essential fields
 * Use this instead of usePlayers when you only need name/id for display
 */
export function usePlayersLookup(options: {
    leagueId?: string | string[];
    season?: string;
    enabled?: boolean;
} = {}) {
    const { leagueId, season, enabled = true } = options;
    const targetLeagueId = Array.isArray(leagueId) ? leagueId[0] : leagueId;

    return useQuery<Map<string, { id: string; firstName: string; lastName: string; firstNameAr?: string; lastNameAr?: string; currentTeamId?: string }>>({
        queryKey: [PLAYERS_LOOKUP_QUERY_KEY_PREFIX, { leagueId: targetLeagueId, season }],
        queryFn: async () => {
            if (!targetLeagueId) {
                return new Map();
            }

            // Fetch only minimal fields needed for lookups
            const { data, error } = await supabase.rpc('get_league_players', {
                p_league_id: targetLeagueId,
                p_season: season || null,
                p_search: null,
                p_page: 1,
                p_page_size: 10000, // Get all players
                p_team_id: null,
                p_include_archived: false
            });

            if (error) {
                console.error("RPC get_league_players failed:", error);
                return new Map();
            }

            // Build lookup map
            const lookupMap = new Map();
            for (const row of (data || [])) {
                lookupMap.set(row.id, {
                    id: row.id,
                    firstName: row.first_name,
                    lastName: row.last_name,
                    firstNameAr: row.first_name_ar || undefined,
                    lastNameAr: row.last_name_ar || undefined,
                    currentTeamId: row.current_team_id,
                });
            }
            return lookupMap;
        },
        enabled: enabled && !!targetLeagueId,
        staleTime: PLAYERS_STALE_TIME,
        gcTime: PLAYERS_GC_TIME,
        placeholderData: keepPreviousData,
    });
}


export function useCreatePlayer() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (player: any) => {
            const dbPayload = mapPlayerToDb(player);
            const { data, error } = await supabase
                .from('players')
                .insert(dbPayload)
                .select(PLAYER_LIST_COLUMNS)
                .single();

            if (error) return logAndThrow('create player', error, { player });
            // Map DB snake_case back to camelCase Player
            if (data) {
                return {
                    id: data.id,
                    firstName: data.first_name,
                    lastName: data.last_name,
                    fullName: data.full_name || `${data.first_name} ${data.last_name}`,
                    firstNameAr: data.first_name_ar || undefined,
                    lastNameAr: data.last_name_ar || undefined,
                    // Pas relue (donnée personnelle) : c'est celle que l'on vient d'écrire.
                    dateOfBirth: dbPayload.date_of_birth ?? undefined,
                    licenseNumber: data.license_number,
                    playerNumber: data.player_number,
                    currentTeamId: data.current_team_id,
                    isArchived: data.is_archived,
                    createdAt: data.created_at,
                    createdBy: data.created_by,
                    createdByName: undefined,
                    updatedAt: data.updated_at,
                    updatedBy: data.updated_by,
                    updatedByName: undefined,
                } as Player;
            }
            return data as any;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [PLAYERS_KEY] });
        },
    });
}
